import type {
  ChatPlayerCompiledChat,
  ChatPlayerMessage,
  ChatPlayerMessageSnapshot,
  ChatPlayerPart,
  ChatPlayerTimelineEvent,
  ChatPlayerToolStatus,
} from "./chat-player-types";
import { clampMs } from "./chat-player-timing";

export const CHAT_PLAYER_TOOL_STATUS_TRANSITION_MS = 360;
export const CHAT_PLAYER_IDLE_TIME_MS = -1;

type ChatPlayerToolPart = Extract<ChatPlayerPart, { type: "tool" }>;
type ChatPlayerToolTimelineEvent = Extract<ChatPlayerTimelineEvent, { type: "tool" }>;

export type ChatPlayerPlaybackFrame = {
  durationMs: number;
  timeMs: number;
  toolEventByPartKey: Map<string, ChatPlayerToolTimelineEvent>;
  visibleMessageIds: Set<string>;
  visiblePartKeys: Set<string>;
  visibleWordKeys: Set<string>;
};

export function getChatPlayerSnapshot(chat: ChatPlayerCompiledChat, timeMs: number) {
  return getChatPlayerSnapshotFromFrame(chat, getChatPlayerPlaybackFrame(chat, timeMs));
}

export function getChatPlayerPlaybackFrame(chat: ChatPlayerCompiledChat, timeMs: number) {
  const normalizedTimeMs = normalizeSnapshotTime(timeMs, chat.durationMs);
  const frame: ChatPlayerPlaybackFrame = {
    durationMs: chat.durationMs,
    timeMs: normalizedTimeMs,
    toolEventByPartKey: new Map(),
    visibleMessageIds: new Set(),
    visiblePartKeys: new Set(),
    visibleWordKeys: new Set(),
  };

  if (normalizedTimeMs < 0) {
    return frame;
  }

  const visibleEvents = getEventsThroughTime(chat.timeline, normalizedTimeMs);

  for (const event of visibleEvents) {
    switch (event.type) {
      case "message":
        frame.visibleMessageIds.add(event.messageId);
        break;
      case "part":
        frame.visibleMessageIds.add(event.messageId);
        frame.visiblePartKeys.add(getChatPlayerPartKey(event.messageId, event.partId));
        break;
      case "word":
        frame.visibleWordKeys.add(wordKey(event.messageId, event.partId, event.wordId));
        break;
      case "tool":
        frame.toolEventByPartKey.set(getChatPlayerPartKey(event.messageId, event.partId), event);
        break;
    }
  }

  return frame;
}

export function getChatPlayerSnapshotFromFrame(chat: ChatPlayerCompiledChat, frame: ChatPlayerPlaybackFrame) {
  const messages: ChatPlayerMessageSnapshot[] = getChatPlayerVisibleMessagesFromFrame(chat, frame).map(message => ({
    id: message.id,
    role: message.role,
    parts: getChatPlayerVisiblePartsFromFrame(message, frame).map(part =>
      getChatPlayerPartSnapshotFromFrame(frame, message.id, part),
    ),
  }));

  return {
    timeMs: frame.timeMs,
    durationMs: frame.durationMs,
    messages,
  };
}

export function getChatPlayerVisibleMessagesFromFrame(chat: ChatPlayerCompiledChat, frame: ChatPlayerPlaybackFrame) {
  return chat.messages.filter(message => frame.visibleMessageIds.has(message.id));
}

export function getChatPlayerVisiblePartsFromFrame(message: ChatPlayerMessage, frame: ChatPlayerPlaybackFrame) {
  return message.parts.filter(part => frame.visiblePartKeys.has(getChatPlayerPartKey(message.id, part.id)));
}

export function getChatPlayerVisiblePartKeysFromFrame(chat: ChatPlayerCompiledChat, frame: ChatPlayerPlaybackFrame) {
  return getChatPlayerVisibleMessagesFromFrame(chat, frame).flatMap(message =>
    getChatPlayerVisiblePartsFromFrame(message, frame).map(part => getChatPlayerPartKey(message.id, part.id)),
  );
}

export function getChatPlayerForegroundPartKeysFromFrame(
  chat: ChatPlayerCompiledChat,
  frame: ChatPlayerPlaybackFrame,
  maxVisibleParts: number,
) {
  if (maxVisibleParts <= 0) {
    return [];
  }

  return getChatPlayerVisiblePartKeysFromFrame(chat, frame).slice(-maxVisibleParts);
}

export function getChatPlayerVisibleSpeechWordIndexFromFrame(
  frame: ChatPlayerPlaybackFrame,
  messageId: string,
  part: Extract<ChatPlayerPart, { type: "speech" }>,
) {
  let visibleWordIndex = -1;

  part.words.forEach((word, index) => {
    if (frame.visibleWordKeys.has(wordKey(messageId, part.id, word.id))) {
      visibleWordIndex = index;
    }
  });

  return visibleWordIndex;
}

export function getChatPlayerToolStatusFromFrame(
  frame: ChatPlayerPlaybackFrame,
  messageId: string,
  part: ChatPlayerToolPart,
) {
  const timing = frame.toolEventByPartKey.get(getChatPlayerPartKey(messageId, part.id));
  const isLoading = !timing || frame.timeMs < getToolStatusSwitchMs(timing.startMs, timing.endMs);
  const status: ChatPlayerToolStatus = isLoading ? "loading" : part.data.result === "error" ? "error" : "success";

  return status;
}

function getChatPlayerPartSnapshotFromFrame(frame: ChatPlayerPlaybackFrame, messageId: string, part: ChatPlayerPart) {
  switch (part.type) {
    case "text":
    case "custom":
      return part;
    case "tool":
      return getChatPlayerToolPartSnapshot(part, getChatPlayerToolStatusFromFrame(frame, messageId, part));
    case "speech":
      return getChatPlayerSpeechPartSnapshot(
        part,
        getChatPlayerVisibleSpeechWordIndexFromFrame(frame, messageId, part),
      );
  }
}

export function getChatPlayerSpeechPartSnapshot(
  part: Extract<ChatPlayerPart, { type: "speech" }>,
  visibleWordIndex: number,
) {
  return {
    id: part.id,
    type: "speech",
    words: part.words.map((word, index) => ({
      ...word,
      isVisible: index <= visibleWordIndex,
    })),
  } satisfies Extract<ChatPlayerMessageSnapshot["parts"][number], { type: "speech" }>;
}

export function getChatPlayerToolPartSnapshot(part: ChatPlayerToolPart, status: ChatPlayerToolStatus) {
  return {
    id: part.id,
    type: "tool",
    name: part.name,
    data: part.data,
    status,
  } satisfies Extract<ChatPlayerMessageSnapshot["parts"][number], { type: "tool" }>;
}

export function getNextChatPlayerUpdateTimeMs(chat: ChatPlayerCompiledChat, timeMs: number) {
  const normalizedTimeMs = normalizeSnapshotTime(timeMs, chat.durationMs);
  let nextTimeMs = Number.POSITIVE_INFINITY;

  for (const event of chat.timeline) {
    if (event.startMs > normalizedTimeMs) {
      nextTimeMs = Math.min(nextTimeMs, event.startMs);
    }

    if (event.type === "tool") {
      const statusSwitchMs = getToolStatusSwitchMs(event.startMs, event.endMs);

      if (statusSwitchMs > normalizedTimeMs) {
        nextTimeMs = Math.min(nextTimeMs, statusSwitchMs);
      }
    }
  }

  return Number.isFinite(nextTimeMs) ? nextTimeMs : null;
}

function normalizeSnapshotTime(timeMs: number, durationMs: number) {
  if (Number.isFinite(timeMs) && timeMs < 0) {
    return Math.round(timeMs);
  }

  return clampMs(timeMs, 0, durationMs);
}

function getToolStatusSwitchMs(startMs: number, endMs: number) {
  return Math.max(startMs, endMs - CHAT_PLAYER_TOOL_STATUS_TRANSITION_MS);
}

function getEventsThroughTime(timeline: ChatPlayerTimelineEvent[], timeMs: number) {
  const endIndex = upperBoundTimeline(timeline, timeMs);

  return timeline.slice(0, endIndex);
}

function upperBoundTimeline(timeline: ChatPlayerTimelineEvent[], timeMs: number) {
  let low = 0;
  let high = timeline.length;

  while (low < high) {
    const mid = Math.floor((low + high) / 2);

    if (timeline[mid].startMs <= timeMs) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  return low;
}

export function getChatPlayerPartKey(messageId: string, partId: string) {
  return `${messageId}:${partId}`;
}

function wordKey(messageId: string, partId: string, wordId: string) {
  return `${messageId}:${partId}:${wordId}`;
}
